import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerActions, DrawerView } from '@divvy-web/skylab.drawer'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import Scrim from '@divvy-web/skylab.scrim'
import Spinner from '@divvy-web/skylab.spinner'

import { AddPersonDrawerOpenable } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { LimitedAddPersonForm, limitedAddPersonForm } from '@sputnik/schema/creditApplication/addPerson'

import styles from './styles.module.scss'
export interface LimitedAddPersonDrawerProps extends AddPersonDrawerOpenable {
  title: React.ReactNode
  initialValues?: () => Promise<LimitedAddPersonForm>
  onSubmit: (data: LimitedAddPersonForm) => Promise<void>
}

const LimitedAddPersonDrawer = ({
  isShowing,
  onClose,
  onSubmit,
  title,
  initialValues,
}: LimitedAddPersonDrawerProps) => {
  const [getClassName] = useNameFormatter('LimitedAddPersonDrawer')

  const { handleSubmit, reset, control } = useForm<LimitedAddPersonForm>({
    mode: 'all',
    defaultValues: initialValues ? async () => initialValues() : {},
    resolver: yupResolver(limitedAddPersonForm),
  })

  const { isValid, isDirty, isSubmitting, isLoading } = useFormState({ control })

  const onCloseForm = () => {
    reset()
    onClose()
  }

  return (
    <Scrim isShowing={isShowing}>
      <Drawer
        activeViewKey='default'
        className={`${styles[getClassName('')]} fs-unmask`}
        isShowing={isShowing}
        views={{
          default: (
            <DrawerView
              footerActions={
                <DrawerActions
                  cancelText={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Cancel'
                        id='sputnik.LimitedAddPersonDrawer__47FYwb'
                      />
                    </span>
                  }
                  className={styles[getClassName('actionButtons')]}
                  confirmButtonColor='action'
                  confirmText={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Save'
                        id='sputnik.LimitedAddPersonDrawer__jvo0vs'
                      />
                    </span>
                  }
                  form={getClassName('form')}
                  inactive={!isValid || !isDirty || isSubmitting}
                  onCancel={onCloseForm}
                  onConfirm={handleSubmit(onSubmit)}
                />
              }
              headerLeftIcon='close'
              headerLeftIconName='close'
              title={title}
              onLeftIconClick={onCloseForm}
            >
              {isLoading ? (
                <Spinner centeredWithAbsolutePositioning />
              ) : (
                <form
                  className={styles[getClassName('form')]}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <FormTextInput
                    className='fs-mask'
                    control={control}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='First name'
                          id='sputnik.LimitedAddPersonDrawer__pONqz8'
                        />
                      </span>
                    }
                    name='firstName'
                  />
                  <FormTextInput
                    className='fs-mask'
                    control={control}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='Last name'
                          id='sputnik.LimitedAddPersonDrawer__txUL0F'
                        />
                      </span>
                    }
                    name='lastName'
                  />
                  <FormTextInput
                    className='fs-mask'
                    control={control}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='Business email'
                          id='sputnik.LimitedAddPersonDrawer__De2rFS'
                        />
                      </span>
                    }
                    name='email'
                  />
                </form>
              )}
            </DrawerView>
          ),
        }}
      />
    </Scrim>
  )
}

export default LimitedAddPersonDrawer
